<template>
  <div class="panel-header">
    <!-- Back Button -->
    <v-btn
      v-if="showBack"
      icon
      small
      class="header-btn back-btn"
      @click="$emit('back')"
      aria-label="Go back"
    >
      <v-icon size="18">mdi-arrow-left</v-icon>
    </v-btn>

    <!-- Title -->
    <h3 class="panel-title" :class="{ 'with-back': showBack }">
      <slot name="title">
        {{ title }}
      </slot>
    </h3>

    <v-spacer />

    <!-- Actions Slot -->
    <slot name="actions" />

    <!-- Close Button -->
    <v-tooltip v-if="showClose" bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          small
          v-bind="attrs"
          v-on="on"
          class="header-btn close-btn"
          @click="$emit('close')"
          aria-label="Close panel"
        >
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </template>
      <span>Close panel</span>
    </v-tooltip>
  </div>
</template>

<script>
/**
 * PanelHeader Component
 *
 * A reusable header for content panels with back and close controls.
 * Panels are always pinned by default (no auto-collapse on click-outside).
 *
 * @props {String} title - Panel title text
 * @props {Boolean} showBack - Show back button (for hierarchical panels)
 * @props {Boolean} showClose - Show close button
 *
 * @emits back - When back button is clicked
 * @emits close - When close button is clicked
 */
export default {
  name: 'PanelHeader',

  props: {
    title: {
      type: String,
      default: '',
    },
    showBack: {
      type: Boolean,
      default: false,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.panel-header {
  display: flex;
  align-items: center;
  padding: var(--ds-space-sm) var(--ds-space-md);
  min-height: 48px;
  border-bottom: 1px solid var(--ds-border-subtle);
  background: inherit;
  gap: var(--ds-space-xs);
}

.panel-title {
  font-size: var(--ds-font-title);
  font-weight: 600;
  color: var(--v-grey-base);
  letter-spacing: 0.3px;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;

  &.with-back {
    margin-left: var(--ds-space-xs);
  }
}

.header-btn {
  flex-shrink: 0;
  transition: all var(--ds-transition-fast);

  &.back-btn {
    margin-left: calc(-1 * var(--ds-space-xs));
  }

  &.close-btn {
    &:hover {
      color: var(--v-error-base);
    }
  }
}
</style>
